<template>
  <v-card class="tn-grid">
    <div class="tn-grid__head">
      <div class="tn-grid__heading">
        <span class="tn-grid__icon"><v-icon size="22">{{ config.icon }}</v-icon></span>
        <div class="tn-grid__titles">
          <h2 class="tn-grid__title">{{ config.title }}</h2>
          <div class="tn-grid__count">
            <template v-if="loading && !items.length">Loading…</template>
            <template v-else>{{ countLabel }}</template>
          </div>
        </div>
      </div>
      <div class="tn-grid__tools">
        <v-text-field
          v-model="search"
          class="tn-grid__search"
          placeholder="Search"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          :aria-label="'Search ' + config.title"
        ></v-text-field>
        <v-btn v-if="config.action" depressed color="primary" class="tn-grid__action" @click="runAction">
          <v-icon left size="20">mdi-plus</v-icon>
          {{ config.action.label }}
        </v-btn>
      </div>
    </div>

    <v-data-table
      :headers="tableHeaders"
      :items="items"
      item-key="id"
      :items-per-page="15"
      :footer-props="{ 'items-per-page-options': [15, 30, 50, -1] }"
      :loading="loading"
      loading-text="Loading… Please wait"
      :search="search"
      :hide-default-footer="serverPaged"
      :mobile-breakpoint="0"
      class="tn-grid__table"
    >
      <!-- shared row actions -->
      <template v-if="type === 'cities'" v-slot:item.editCity="{ item }">
        <row-action icon="mdi-pencil-outline" label="Edit city" color="primary" @click="$emit('updateCity', item)" />
      </template>
      <template v-if="type === 'cities'" v-slot:item.removeCityById="{ item }">
        <row-action icon="mdi-trash-can-outline" label="Delete city" color="error" @click="$emit('removeCity', item.id)" />
      </template>
      <template v-if="type === 'cities'" v-slot:item.image_url="{ item }">
        <thumb :src="item.image_url" />
      </template>
      <template v-if="type === 'cities'" v-slot:item.name="{ item }">
        <span class="font-weight-medium">{{ item.name }}</span>
      </template>
      <template v-if="type === 'cities'" v-slot:item.airport_code="{ item }">
        <span v-if="item.airport_code && item.airport_code !== 'null'" :title="item.airport_name">{{ item.airport_code }}</span>
        <span v-else class="tn-muted">—</span>
      </template>
      <template v-if="type === 'cities'" v-slot:item.slug="{ item }">
        <code class="tn-code">{{ item.slug }}</code>
      </template>

      <!-- SEO -->
      <template v-if="type === 'seo'" v-slot:item.featured_image="{ item }">
        <thumb :src="item.featured_image" />
      </template>
      <template v-if="type === 'seo'" v-slot:item.page_name="{ item }">
        <span class="font-weight-medium">{{ item.page_name }}</span>
      </template>
      <template v-if="type === 'seo'" v-slot:item.slug="{ item }">
        <code class="tn-code">{{ item.slug }}</code>
      </template>
      <template v-if="type === 'seo'" v-slot:item.editSEO="{ item }">
        <row-action icon="mdi-pencil-outline" label="Edit SEO" color="primary" @click="$emit('updateSEO', item)" />
      </template>

      <!-- tours -->
      <template v-if="type === 'tours'" v-slot:item.packageImage="{ item }">
        <thumb :src="item.packageImage" />
      </template>
      <template v-if="type === 'tours'" v-slot:item.packageTitle="{ item }">
        <button type="button" class="tn-link" @click="goToRoute(item.packageID)">{{ item.packageTitle }}</button>
      </template>
      <template v-if="type === 'tours'" v-slot:item.removeTour="{ item }">
        <row-action icon="mdi-trash-can-outline" label="Delete package" color="error" @click="$emit('removeTour', item.packageID)" />
      </template>
      <template v-if="type === 'tours'" v-slot:item.packagePricePerPerson="{ item }">
        {{ money(item.packagePricePerPerson) }}
      </template>
      <template v-if="type === 'tours'" v-slot:item.destination="{ item }">
        <span v-for="(destination, i) in item.packageCities" :key="destination.packageCityID">
          {{destination.cityName}}<span v-if="i < item.packageCities.length - 1">, </span>
        </span>
      </template>

      <!-- hotels -->
      <template v-if="type === 'hotels'" v-slot:item.hotelName="{ item }">
        <router-link class="tn-link" :to="{ name: 'hotel', params: { id: item.hotelID } }">{{ item.hotelName }}</router-link>
      </template>
      <template v-if="type === 'hotels'" v-slot:item.hotelImage="{ item }">
        <thumb :src="item.hotelImage" />
      </template>
      <template v-if="type === 'hotels'" v-slot:item.removeHotel="{ item }">
        <row-action icon="mdi-trash-can-outline" label="Delete hotel" color="error" @click="$emit('removeHotel', item.hotelID)" />
      </template>

      <!-- activities -->
      <template v-if="type === 'activities'" v-slot:item.activityTitle="{ item }">
        <button type="button" class="tn-link" @click="$emit('showActivity', item)">{{ item.activityTitle }}</button>
      </template>
      <template v-if="type === 'activities'" v-slot:item.published="{ item }">
        <v-chip small label class="tn-status-chip" :class="item.published ? 'tn-status-chip--success' : 'tn-status-chip--grey'">
          {{ item.published ? 'Published' : 'Draft' }}
        </v-chip>
      </template>
      <template v-if="type === 'activities'" v-slot:item.activityImage="{ item }">
        <thumb :src="item.activityImage" />
      </template>
      <template v-if="type === 'activities'" v-slot:item.activityDuration_digits="{ item }">
        <span>{{item.activityDuration_digits}} {{item.activityDuration_type}}</span>
      </template>
      <template v-if="type === 'activities'" v-slot:item.editActive="{ item }">
        <row-action icon="mdi-pencil-outline" label="Edit activity" color="primary" @click="$emit('editActivity', item)" />
      </template>
      <template v-if="type === 'activities'" v-slot:item.removeActive="{ item }">
        <row-action icon="mdi-trash-can-outline" label="Delete activity" color="error" @click="$emit('removeActivity', item.activityID)" />
      </template>

      <!-- cruises -->
      <template v-if="type === 'cruises'" v-slot:item.name="{ item }">
        <router-link class="tn-link" :to="{ name: 'cruise', params: { id: item.id } }">{{ item.name }}</router-link>
      </template>
      <template v-if="type === 'cruises'" v-slot:item.stars="{ item }">
        <span v-if="item.stars" class="tn-stars"><v-icon size="16" color="amber darken-1">mdi-star</v-icon>{{ item.stars }}</span>
        <span v-else>—</span>
      </template>
      <template v-if="type === 'cruises'" v-slot:item.removeAct="{ item }">
        <row-action icon="mdi-trash-can-outline" label="Delete cruise" color="error" @click="$emit('removeCruise', item.id)" />
      </template>

      <!-- package bookings -->
      <template v-if="type === 'packageBookings'" v-slot:item.packageId="{ item }">
        <router-link class="tn-link tn-link--id" :to="{ name: 'packageBooking', params: { id: item.id } }">#{{ item.id }}</router-link>
      </template>
      <template v-if="type === 'packageBookings'" v-slot:item.package="{ item }">
        <router-link v-if="item.package" class="tn-link" :to="{ name: 'tour', params: { id: item.package.id } }">{{ item.package.title }}</router-link>
        <span v-else>—</span>
      </template>

      <!-- adventure / cruise / hotel bookings -->
      <template v-if="bookingRoute" v-slot:item.id="{ item }">
        <router-link class="tn-link tn-link--id" :to="{ name: bookingRoute, params: { id: item.id } }">#{{ item.id }}</router-link>
      </template>

      <!-- every booking list -->
      <template v-if="isBookingList" v-slot:item.total_price="{ item }">
        <span class="font-weight-medium">{{ money(item.total_price) }}</span>
      </template>
      <template v-if="isBookingList" v-slot:item.status="{ item }">
        <v-chip small label class="tn-status-chip" :class="'tn-status-chip--' + statusColor(item.status)">{{ statusLabel(item.status) }}</v-chip>
      </template>
      <template v-if="isBookingList" v-slot:item.created_at="{ item }">
        <span class="text-no-wrap">{{ date(item.created_at) }}</span>
      </template>

      <template v-slot:no-data>
        <div class="tn-empty">
          <v-icon size="40">mdi-inbox-outline</v-icon>
          <div>No {{ config.nouns }} yet</div>
        </div>
      </template>
      <template v-slot:no-results>
        <div class="tn-empty">
          <v-icon size="40">mdi-magnify-close</v-icon>
          <div>No {{ config.nouns }} match “{{ search }}”</div>
        </div>
      </template>
    </v-data-table>

    <div v-if="serverPaged && pagesNum() > 1" class="tn-grid__pager">
      <span class="tn-grid__pager-info">Page {{ currentPage }} of {{ pagesNum() }}</span>
      <v-pagination
        v-model="currentPage"
        :length="pagesNum()"
        total-visible="7"
        @input="pageNumber"
      ></v-pagination>
    </div>
  </v-card>
</template>

<script>
import { VBtn, VIcon, VTooltip } from 'vuetify/lib'
import { formatDate, formatMoney, statusColor, statusLabel } from '../utils/format'

const types = {
  cities: { icon: 'mdi-city-variant-outline', title: 'Trip Cities', noun: 'city', nouns: 'cities', action: { label: 'Add city', emit: 'addCity' } },
  seo: { icon: 'mdi-web', title: 'SEO', noun: 'page', nouns: 'pages', action: { label: 'Add SEO', emit: 'addSEO' } },
  tours: { icon: 'mdi-beach', title: 'Packages', noun: 'package', nouns: 'packages', action: { label: 'Add package', route: 'addPackage' } },
  hotels: { icon: 'mdi-bed-outline', title: 'Hotels', noun: 'hotel', nouns: 'hotels', action: { label: 'Add hotel', route: 'addHotel' } },
  activities: { icon: 'mdi-hiking', title: 'Adventures', noun: 'activity', nouns: 'activities', action: { label: 'Add activity', emit: 'addActivity' } },
  cruises: { icon: 'mdi-ferry', title: 'Cruises', noun: 'cruise', nouns: 'cruises', action: { label: 'Add cruise', route: 'addCruise' } },
  packageBookings: { icon: 'mdi-ticket-confirmation-outline', title: 'Package Bookings', noun: 'booking', nouns: 'bookings' },
  activitiesBookings: { icon: 'mdi-compass-outline', title: 'Adventure Bookings', noun: 'booking', nouns: 'bookings', detail: 'activityBooking' },
  cruisesBookings: { icon: 'mdi-sail-boat', title: 'Cruise Bookings', noun: 'booking', nouns: 'bookings', detail: 'cruiseBooking' },
  hotelsBookings: { icon: 'mdi-office-building-outline', title: 'Hotel Bookings', noun: 'booking', nouns: 'bookings', detail: 'hotelBooking' }
}

// Edit / remove columns render as icon buttons: no header text, no sorting, kept narrow.
const actionColumns = ['editCity', 'removeCityById', 'editSEO', 'removeTour', 'removeHotel', 'editActive', 'removeActive', 'removeAct']

// Lists the API pages itself (15 per page); the rest are paged in the browser.
const serverPagedTypes = ['hotels', 'activities', 'tours', 'cruises']

const RowAction = {
  functional: true,
  props: ['icon', 'label', 'color'],
  render (h, { props, listeners }) {
    return h(VTooltip, { props: { top: true, openDelay: 300 }, scopedSlots: {
      activator: ({ on, attrs }) => h(VBtn, {
        props: { icon: true, small: true, color: props.color },
        attrs: { ...attrs, 'aria-label': props.label },
        class: 'tn-row-action',
        on: { ...on, click: listeners.click }
      }, [h(VIcon, { props: { size: 19 } }, props.icon)])
    } }, [h('span', props.label)])
  }
}

const Thumb = {
  functional: true,
  props: ['src'],
  render (h, { props }) {
    const children = [h(VIcon, { props: { size: 20 } }, 'mdi-image-outline')]
    if (props.src) {
      children.push(h('img', {
        attrs: { src: props.src, alt: '', loading: 'lazy' },
        on: { error: event => { event.target.style.display = 'none' } }
      }))
    }
    return h('span', { class: 'tn-thumb' }, children)
  }
}

export default {
  components: { RowAction, Thumb },
  props: ['headers', 'items', 'type', 'loading', 'blockBtnLoad', 'bookingStatuses', 'paymentStatuses', 'unblockBtnLoad', 'filters', 'dates', 'statuses', 'total', 'page', 'length'],
  data () {
    return {
      search: '',
      currentPage: this.page || 1
    }
  },
  computed: {
    config () {
      return types[this.type] || { icon: 'mdi-table', title: '', noun: 'item', nouns: 'items' }
    },
    serverPaged () {
      return serverPagedTypes.includes(this.type)
    },
    isBookingList () {
      return /Bookings$/.test(this.type || '')
    },
    bookingRoute () {
      return this.config.detail || null
    },
    tableHeaders () {
      return (this.headers || []).map(header => actionColumns.includes(header.value)
        ? { ...header, text: '', sortable: false, align: 'end', width: 56, class: 'tn-col-action', cellClass: 'tn-col-action' }
        : header)
    },
    countLabel () {
      const count = this.serverPaged && this.total ? this.total : (this.items || []).length
      const noun = count === 1 ? this.config.noun : this.config.nouns
      return `${Number(count).toLocaleString('en-US')} ${noun}`
    }
  },
  watch: {
    page (val) {
      this.currentPage = val || 1
    }
  },
  methods: {
    statusColor,
    statusLabel,
    date (value) {
      return formatDate(value)
    },
    money (value) {
      return formatMoney(value)
    },
    runAction () {
      const action = this.config.action
      if (action.route) this.$router.push({ name: action.route })
      else this.$emit(action.emit)
    },
    pagesNum () {
      const num = this.total / 15
      const total = Math.ceil(num)
      return total
    },
    pageNumber (num) {
      this.$emit('pageNum', num)
    },
    goToRoute (id) {
      let route
      if (this.type === 'tours') route = 'editPackage'
      else if (this.type === 'hotels') route = 'hotel'
      this.$router.push({ name: route, params: { id: id } })
    }
  }
}
</script>

<style lang="scss">
.tn-grid.v-card {
  overflow: hidden;
}

.tn-grid__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--tn-border);
}

.tn-grid__heading {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.tn-grid__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #FDF0E6;

  .v-icon {
    color: var(--v-orange-base) !important;
  }
}

.tn-grid__title {
  margin: 0;
  color: var(--tn-text);
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -.01em;
  line-height: 1.3;
}

.tn-grid__count {
  color: var(--tn-muted);
  font-size: 13px;
}

.tn-grid__tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  @media (max-width: 599px) {
    width: 100%;
  }
}

.tn-grid__search.v-text-field {
  width: 280px;
  max-width: 100%;

  @media (max-width: 599px) {
    width: 100%;
  }
}

.tn-grid__action.v-btn {
  height: 40px !important;
}

.tn-grid__table td.tn-col-action,
.tn-grid__table th.tn-col-action {
  width: 56px;
  padding: 0 8px !important;
}

.tn-grid__table td.tn-col-action + td.tn-col-action,
.tn-grid__table th.tn-col-action + th.tn-col-action {
  padding-left: 0 !important;
}

.tn-grid__table th.tn-col-action:last-child,
.tn-grid__table td.tn-col-action:last-child {
  padding-right: 16px !important;
}

// Narrow screens scroll the table sideways instead of stacking every row.
@media (max-width: 959px) {
  .tn-grid__table td {
    white-space: nowrap;
  }

  .tn-grid__table td .tn-link:not(.tn-link--id) {
    display: inline-block;
    min-width: 220px;
    white-space: normal;
  }
}

.tn-grid__pager {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px 10px 24px;
  border-top: 1px solid var(--tn-border);

  .v-pagination {
    width: auto;
  }

  .v-pagination__item,
  .v-pagination__navigation {
    box-shadow: none;
    border: 1px solid var(--tn-border);
  }

  .v-pagination__item--active {
    border-color: transparent;
  }
}

.tn-grid__pager-info {
  color: var(--tn-muted);
  font-size: 13px;
}

.tn-link {
  padding: 0;
  border: 0;
  background: none;
  color: var(--v-primary-base) !important;
  font: inherit;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &--id {
    font-variant-numeric: tabular-nums;
  }
}

.tn-thumb {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 40px;
  overflow: hidden;
  vertical-align: middle;
  border-radius: 8px;
  background: #EEF1F5;

  .v-icon {
    color: #A0AEC0 !important;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.tn-code {
  padding: 2px 6px;
  border-radius: 6px;
  background: #F1F5F9;
  color: var(--tn-text-2);
  font-size: 12.5px;
}

.tn-stars {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-weight: 600;
}

.tn-row-action.v-btn {
  opacity: .85;

  &:hover {
    opacity: 1;
  }
}
</style>
